import { Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useState, type ComponentProps } from 'react';
import { useCommendaAiHost } from '../CommendaAiHostProvider';
import { ChatReferenceHelper } from './ChatReferenceHelper';
import { ChatReferencePopover } from './ChatReferencePopover';
import { ExternalLinkModal } from './ExternalLinkModal';
import { MarkdownLinkHelper } from './MarkdownLinkHelper';

// streamdown passes `node` (the HAST node) alongside the standard anchor props; it must never reach
// the DOM.
type MarkdownLinkProps = ComponentProps<'a'> & { node?: unknown };

/** Renders a markdown link. Image and PDF targets show an inline preview instead of a bare link;
 * Same-origin Commenda targets route in-app; other Commenda hosts keep their full URL.
 * Every other link opens the app-styled
 * {@link ExternalLinkModal} confirmation on click.
 *
 * A Commenda link that names a record the agent can preview renders as an inline reference instead:
 * prose-coloured with a hairline underline, opening {@link ChatReferencePopover} on hover. Reserving
 * that treatment for previewable records is what makes the underline mean "hover me". */
export function MarkdownLink({
  href,
  children,
  node: _node,
  className,
  ...rest
}: MarkdownLinkProps) {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const { navigate, openRecord } = useCommendaAiHost();

  if (!href) return <span className={className}>{children}</span>;

  const kind = MarkdownLinkHelper.classify(href);
  const fileName = MarkdownLinkHelper.getFileName(href, 'Attachment');

  if (kind === 'image')
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="my-sm block w-fit max-w-full"
      >
        {/* Dynamic signed S3 URLs can't go through next/image — same as AttachmentBadge. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={href}
          alt={typeof children === 'string' ? children : fileName}
          className="max-h-96 max-w-full rounded-lg border border-border-primary object-contain"
        />
      </a>
    );

  if (kind === 'pdf')
    return (
      <span className="my-sm block overflow-hidden rounded-lg border border-border-primary bg-bg-primary">
        <span className="flex items-center justify-between gap-md border-b border-border-primary px-lg py-md">
          <span className="flex min-w-0 items-center gap-sm">
            <Icons.File02 className="size-4 shrink-0 text-fg-tertiary" />
            <span className="truncate txt-small-medium text-text-primary">
              {fileName}
            </span>
          </span>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-xs txt-extra-small-medium text-fg-brand hover:underline"
          >
            <Icons.LinkExternal01 className="size-3.5" />
            Open
          </a>
        </span>
        <object data={href} type="application/pdf" className="h-96 w-full">
          <span className="flex h-96 items-center justify-center px-lg text-center txt-small-regular text-text-tertiary">
            Preview unavailable — open the PDF in a new tab.
          </span>
        </object>
      </span>
    );

  const linkClassName = cn(
    'font-medium text-fg-brand underline underline-offset-2 hover:text-fg-brand/80',
    className
  );

  const referenceClassName = cn(
    'font-semibold text-text-primary underline decoration-border-primary decoration-[1.25px] underline-offset-2 hover:decoration-fg-brand data-[state=open]:decoration-fg-brand',
    className
  );

  if (MarkdownLinkHelper.isFirstParty(href)) {
    const reference = ChatReferenceHelper.classify(href);

    // Routed rather than opened as a plain anchor, so the console switches into the client the agent named
    // before the destination renders.
    const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      const target = MarkdownLinkHelper.toInternalTarget(
        href,
        window.location.origin
      );
      if (
        !target ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button !== 0
      )
        return;
      event.preventDefault();
      if (openRecord) openRecord(target);
      else navigate(target.path);
    };

    const anchor = (
      <a
        href={href}
        onClick={onLinkClick}
        rel="noopener noreferrer"
        className={reference ? referenceClassName : linkClassName}
        {...rest}
      >
        {children}
      </a>
    );

    if (!reference) return anchor;

    return (
      <ChatReferencePopover reference={reference}>
        {anchor}
      </ChatReferencePopover>
    );
  }

  return (
    <>
      <a
        href={href}
        onClick={(event) => {
          event.preventDefault();
          setConfirmOpen(true);
        }}
        className={linkClassName}
        {...rest}
      >
        {children}
      </a>
      <ExternalLinkModal
        url={href}
        isOpen={isConfirmOpen}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
